import React from 'react';
import { useUser } from '@reducers/user/hooks';
import {
  LastScoreByItemByUserReport,
  ResponseAnalysisByItemReport,
} from 'chalktalk-react/Learnosity/LearnosityReports';
import { useSectionStudent } from '@reducers/sectionStudent/hooks';
import { formatStudentName } from '@utils/studentName';
import { StudentTestletSessionStatus, Testlet } from '../../types';
import { useGetTestletSessionsWithFilter } from '../../hooks';
import NotLockedTestletFallback from './NotLockedTestletFallback';

export const LearnosityExamReport = ({ data }: { data: Testlet }) => {
  const currentUser = useUser();
  const testletSessions = useGetTestletSessionsWithFilter(data?.id);
  const students = useSectionStudent();

  const users = React.useMemo(
    () =>
      testletSessions
        ?.filter(
          (session) =>
            session.status === StudentTestletSessionStatus.COMPLETED &&
            session.started_at != null,
        )
        ?.map((session) => ({
          id: session.user_id.toString(),
          name: formatStudentName(session),
        })) ?? [],
    [testletSessions],
  );

  const usersNotAttempted = React.useMemo(
    () =>
      students
        ?.filter((student) => !users.find(({ id }) => id === student.id.toString()))
        ?.map((student) => ({
          id: student.id.toString(),
          name: formatStudentName(student),
        })) ?? [],
    [students, users],
  );

  return (
    <NotLockedTestletFallback data={data}>
      <LastScoreByItemByUserReport
        teacherId={currentUser?.id}
        reportId={`last-score-by-item-by-user-report-${data.id}`}
        activityId={testletSessions?.[0]?.activity_id ?? ''}
        users={users}
        usersNotAttempted={usersNotAttempted}
      />
    </NotLockedTestletFallback>
  );
};

export const ResponseAnalysisReport = ({ data }: { data: Testlet }) => {
  const currentUser = useUser();
  const students = useSectionStudent();
  const testletSessions = useGetTestletSessionsWithFilter(data?.id);

  const itemReferenceMap = React.useMemo(() => {
    // Find the first session that has scores
    const sessionWithScores = testletSessions?.find(
      (session) => session.scores?.responses && session.scores.responses.length > 0,
    );
    return (
      sessionWithScores?.scores?.responses?.map((response) => ({
        reference: response.item_reference,
        name: response.question_reference,
      })) ?? []
    );
  }, [testletSessions]);

  const users = React.useMemo(
    () =>
      testletSessions
        ?.filter(
          (session) =>
            session.status === StudentTestletSessionStatus.COMPLETED &&
            session.started_at != null,
        )
        ?.map((session) => ({
          id: session.user_id.toString(),
          name: formatStudentName(session),
        })) ?? [],
    [testletSessions],
  );

  const usersNotAttempted = React.useMemo(
    () =>
      students
        ?.filter((student) => !users.find(({ id }) => id === student.id.toString()))
        ?.map((student) => ({
          id: student.id.toString(),
          name: formatStudentName(student),
        })) ?? [],
    [students, users],
  );

  return (
    <ResponseAnalysisByItemReport
      teacherId={currentUser?.id}
      reportId={`response-analysis-by-user-${data.id}`}
      sessionIds={
        testletSessions
          ?.filter(
            (session) =>
              session.status === StudentTestletSessionStatus.COMPLETED &&
              session.started_at != null,
          )
          .map((session) => session.session_id) ?? []
      }
      itemReferenceMap={itemReferenceMap ?? []}
      users={users}
      usersNotAttempted={usersNotAttempted}
    />
  );
};
